import { useState, useEffect, useCallback } from 'react'
import { documentsApi, contactsApi, paiementsApi, produitsApi } from '../api'
import { useApp } from '../AppContext'
import { BadgeStatut, BadgeType, FormGroup, Empty, Loading, fmt, fmtDate, TYPES, STATUTS, Pagination } from '../components/ui'
import { generateDocumentPDF } from '../components/DocumentPDF'

export default function Documents() {
  const { toast } = useApp()
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState({ type: '', statut: '', search: '', page: 1 })
  const [loading, setLoading] = useState(true)
  const [lastPage, setLastPage] = useState(1)
  const [modal, setModal] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    const p = { page: filters.page, per_page: 25 }
    if (filters.type) p.type = filters.type
    if (filters.statut) p.statut = filters.statut
    if (filters.search) p.search = filters.search
    documentsApi.list(p)
      .then(r => { setItems(r.data); setTotal(r.meta?.total || 0); setLastPage(r.meta?.last_page || 1) })
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [filters])

  useEffect(() => { load() }, [load])
  const flt = k => v => setFilters(f => ({ ...f, [k]: v, page: 1 }))

  async function del(id, num) {
    if (!confirm(`Supprimer "${num}" ?`)) return
    try { await documentsApi.delete(id); toast('Supprimé', 'success'); load() }
    catch (e) { toast(e.message, 'error') }
  }

  return (
    <div className="content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="search-box">
          <i className="ti ti-search" />
          <input placeholder="N°, contact, référence…" value={filters.search} onChange={e => flt('search')(e.target.value)} />
        </div>
        <select className="form-control" style={{ width: 170 }} value={filters.type} onChange={e => flt('type')(e.target.value)}>
          <option value="">Tous les types</option>
          {Object.entries(TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="form-control" style={{ width: 160 }} value={filters.statut} onChange={e => flt('statut')(e.target.value)}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUTS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span style={{ color: 'var(--text3)', fontSize: 13, marginLeft: 'auto' }}>{total} doc(s)</span>
        <button className="btn primary" onClick={() => setModal('create')}>
          <i className="ti ti-plus" /> Nouveau document
        </button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading /> : items.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Numéro</th><th>Type</th><th>Contact</th><th>Date</th><th>Échéance</th><th>Montant TTC</th><th>Solde</th><th>Statut</th><th></th></tr>
              </thead>
              <tbody>
                {items.map(doc => (
                  <tr key={doc.id}>
                    <td><strong>{doc.numero}</strong></td>
                    <td><BadgeType type={doc.type} /></td>
                    <td>{doc.contact_nom}</td>
                    <td>{fmtDate(doc.date_document)}</td>
                    <td>{doc.date_echeance ? <span style={{ color: doc.jours_retard > 0 ? 'var(--danger)' : 'inherit' }}>{fmtDate(doc.date_echeance)}</span> : '—'}</td>
                    <td><strong>{fmt(doc.total_ttc)}</strong></td>
                    <td>{parseFloat(doc.solde) > 0 ? <span style={{ color: 'var(--danger)', fontWeight: 500 }}>{fmt(doc.solde)}</span> : <span style={{ color: 'var(--success)' }}>Réglé</span>}</td>
                    <td><BadgeStatut statut={doc.statut} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn sm" onClick={() => setModal({ id: doc.id })} title="Voir"><i className="ti ti-eye" /></button>
                        <button className="btn sm" onClick={async () => {
                          if (!confirm(`Dupliquer ${doc.numero} ?`)) return
                          try {
                            const r = await documentsApi.dupliquer(doc.id)
                            toast(`Dupliqué : ${r.data.numero}`, 'success')
                            load()
                          } catch (e) { toast(e.message, 'error') }
                        }} title="Dupliquer">
                          <i className="ti ti-copy" />
                        </button>
                        <button className="btn sm" onClick={() => del(doc.id, doc.numero)} title="Supprimer"><i className="ti ti-trash" style={{ color: 'var(--danger)' }} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty icon="files" message="Aucun document — créez-en un !" />}
      </div>

      {lastPage > 1 && (
        <Pagination page={filters.page} lastPage={lastPage} onChange={p => setFilters(f => ({ ...f, page: p }))} />
      )}

      {modal === 'create' && <DocumentEditor onClose={() => setModal(null)} onSaved={() => { load(); setModal(null); toast('Document créé', 'success') }} />}
      {modal?.id && <DocumentViewer id={modal.id} onClose={() => setModal(null)} onReload={load} />}
    </div>
  )
}

function DocumentEditor({ doc, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({ type: 'facture', date_document: new Date().toISOString().split('T')[0], statut: 'brouillon', lignes: [], ...(doc || {}) })
  const [contacts, setContacts] = useState([])
  const [produits, setProduits] = useState([])
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  useEffect(() => {
    contactsApi.list({ per_page: 200 }).then(r => setContacts(r.data || [])).catch(() => { })
    produitsApi.list().then(r => setProduits(r.data || [])).catch(() => { })
  }, [])

  const addLigne = () => setForm(p => ({ ...p, lignes: [...(p.lignes || []), { type_ligne: 'produit', designation: '', quantite: 1, prix_ht: 0, remise_pct: 0, tva_taux: 18 }] }))
  const removeLigne = i => setForm(p => ({ ...p, lignes: p.lignes.filter((_, j) => j !== i) }))
  const setLigne = (i, k, v) => setForm(p => ({ ...p, lignes: p.lignes.map((l, j) => j === i ? { ...l, [k]: v } : l) }))
  const selectProduit = (i, produitId) => {
    const p = produits.find(p => String(p.id) === String(produitId))
    if (!p) return
    setForm(prev => ({
      ...prev,
      lignes: prev.lignes.map((l, j) => j !== i ? l : {
        ...l,
        produit_id: p.id,
        designation: p.designation,
        description: p.description || '',
        prix_ht: p.prix_ht,
        tva_taux: p.tva_taux,
        unite: p.unite || '',
      })
    }))
  }

  const totaux = (form.lignes || []).reduce((acc, l) => {
    if (l.type_ligne !== 'produit') return acc
    const ht = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_ht) || 0) * (1 - (parseFloat(l.remise_pct) || 0) / 100)
    const tva = ht * (parseFloat(l.tva_taux) || 18) / 100
    return { ht: acc.ht + ht, tva: acc.tva + tva }
  }, { ht: 0, tva: 0 })

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try { if (doc?.id) await documentsApi.update(doc.id, form); else await documentsApi.create(form); onSaved() }
    catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">{doc?.id ? `Modifier ${doc.numero}` : 'Nouveau document'}</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>

        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="grid3">
              <FormGroup label="Type" required>
                <select className="form-control" value={form.type} onChange={f('type')}>{Object.entries(TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              </FormGroup>
              <FormGroup label="Contact" required>
                <select className="form-control" value={form.contact_id || ''} onChange={f('contact_id')} required>
                  <option value="">Sélectionner…</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </FormGroup>
              <FormGroup label="Date">
                <input type="date" className="form-control" value={form.date_document || ''} onChange={f('date_document')} />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Échéance"><input type="date" className="form-control" value={form.date_echeance || ''} onChange={f('date_echeance')} /></FormGroup>
              <FormGroup label="Réf. client"><input className="form-control" value={form.reference_client || ''} onChange={f('reference_client')} /></FormGroup>
            </div>
            <hr className="divider" />
            <div style={{ overflowX: 'auto' }}>
              <table className="doc-lines">
                <thead>
                  <tr>
                    <th>Produit catalogue</th>
                    <th>Désignation</th>
                    <th style={{ width: 70 }}>Qté</th>
                    <th style={{ width: 110 }}>P.U. HT</th>
                    <th style={{ width: 70 }}>Rem.%</th>
                    <th style={{ width: 70 }}>TVA%</th>
                    <th style={{ width: 110 }}>Total HT</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.lignes || []).map((l, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          style={{ width: '100%', padding: '5px 7px', border: '1px solid transparent', borderRadius: 4, background: 'transparent', fontSize: 12, color: 'var(--text3)' }}
                          value={l.produit_id || ''}
                          onChange={e => selectProduit(i, e.target.value)}
                        >
                          <option value="">— Saisie libre —</option>
                          {produits.map(p => (
                            <option key={p.id} value={p.id}>{p.designation} ({p.prix_ht} HT)</option>
                          ))}
                        </select>
                      </td>
                      <td><input value={l.designation || ''} onChange={e => setLigne(i, 'designation', e.target.value)} placeholder="Description…" /></td>
                      <td><input type="number" value={l.quantite || 1} min={0} step="any" onChange={e => setLigne(i, 'quantite', e.target.value)} /></td>
                      <td><input type="number" value={l.prix_ht || 0} min={0} step="any" onChange={e => setLigne(i, 'prix_ht', e.target.value)} /></td>
                      <td><input type="number" value={l.remise_pct || 0} min={0} max={100} step="any" onChange={e => setLigne(i, 'remise_pct', e.target.value)} /></td>
                      <td><input type="number" value={l.tva_taux || 18} min={0} step="any" onChange={e => setLigne(i, 'tva_taux', e.target.value)} /></td>
                      <td><strong>{fmt((parseFloat(l.quantite) || 0) * (parseFloat(l.prix_ht) || 0) * (1 - (parseFloat(l.remise_pct) || 0) / 100))}</strong></td>
                      <td><button type="button" style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16, padding: '2px 4px' }} onClick={() => removeLigne(i)}><i className="ti ti-x" /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={8} style={{ paddingTop: 8 }}><button type="button" className="btn sm" onClick={addLigne}><i className="ti ti-plus" /> Ajouter une ligne</button></td></tr></tfoot>
              </table>
            </div>
            <div className="doc-total">
              <table><tbody>
                <tr><td>Total HT</td><td style={{ textAlign: 'right' }}>{fmt(totaux.ht)}</td></tr>
                <tr><td>TVA</td><td style={{ textAlign: 'right' }}>{fmt(totaux.tva)}</td></tr>
                <tr className="total-row"><td>Total TTC</td><td style={{ textAlign: 'right' }}>{fmt(totaux.ht + totaux.tva)}</td></tr>
              </tbody></table>
            </div>
            <FormGroup label="Conditions / notes"><textarea className="form-control" rows={2} value={form.conditions || ''} onChange={f('conditions')} /></FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DocumentViewer({ id, onClose, onReload }) {
  const { toast } = useApp()
  const [doc, setDoc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [payModal, setPayModal] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)

  const reload = () => documentsApi.get(id).then(r => setDoc(r.data)).catch(e => toast(e.message, 'error'))

  useEffect(() => {
    documentsApi.get(id).then(r => setDoc(r.data)).catch(e => toast(e.message, 'error')).finally(() => setLoading(false))
  }, [id])

  async function handlePDF() {
    setPdfLoading(true)
    try { const fn = await generateDocumentPDF(doc); toast(`PDF généré : ${fn}`, 'success') }
    catch (e) { toast('Erreur PDF : ' + e.message, 'error') }
    finally { setPdfLoading(false) }
  }

  async function changeStatut(statut) {
    try { await documentsApi.statut(id, statut); setDoc(d => ({ ...d, statut })); onReload(); toast('Statut mis à jour', 'success') }
    catch (e) { toast(e.message, 'error') }
  }

  async function transformer(type) {
    if (!confirm(`Transformer en ${TYPES[type]} ?`)) return
    try { const r = await documentsApi.transformer(id, type); toast(`${TYPES[type]} créé : ${r.data.numero}`, 'success'); onReload(); onClose() }
    catch (e) { toast(e.message, 'error') }
  }

  async function dupliquer() {
    if (!confirm(`Dupliquer le document ${doc.numero} ?`)) return
    try {
      const r = await documentsApi.dupliquer(id)
      toast(`Document dupliqué : ${r.data.numero}`, 'success')
      onReload()
      onClose()
    } catch (e) { toast(e.message, 'error') }
  }

  if (loading) return <div className="overlay"><div className="modal"><Loading /></div></div>
  if (!doc) return null
  if (editing) return <DocumentEditor doc={doc} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); reload(); onReload(); toast('Document mis à jour', 'success') }} />

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="modal-title">{doc.numero}</span>
              <BadgeStatut statut={doc.statut} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}><BadgeType type={doc.type} /> · {doc.contact_nom}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              className="btn sm"
              onClick={async () => {
                setPdfLoading(true)
                try { await generateDocumentPDF(doc, 'preview') }
                catch (e) { toast('Erreur aperçu : ' + e.message, 'error') }
                finally { setPdfLoading(false) }
              }}
              disabled={pdfLoading}
              title="Aperçu PDF"
            >
              <i className="ti ti-eye" /> Aperçu
            </button>
            <button className="btn sm primary" onClick={handlePDF} disabled={pdfLoading}>
              {pdfLoading ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> PDF…</> : <><i className="ti ti-file-type-pdf" /> Télécharger PDF</>}
            </button>
            <button className="btn sm" onClick={() => dupliquer()} title="Dupliquer">
              <i className="ti ti-copy" /> Dupliquer
            </button>
            {doc.type === 'devis' && doc.statut === 'envoye' && <button className="btn sm" onClick={() => transformer('facture')}><i className="ti ti-transform" /> → Facture</button>}
            {doc.type === 'facture' && !['regle', 'annule'].includes(doc.statut) && <button className="btn sm" onClick={() => setPayModal(true)}><i className="ti ti-cash" /> Paiement</button>}
            <button className="btn sm" onClick={() => setEditing(true)}><i className="ti ti-edit" /></button>
            <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
          </div>
        </div>

        <div className="modal-body">
          <div className="grid2" style={{ marginBottom: 16 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, letterSpacing: '.05em', textTransform: 'uppercase' }}>Client</p>
              <p style={{ fontWeight: 600 }}>{doc.contact_nom}</p>
              {doc.contact_adresse && <p style={{ fontSize: 13, color: 'var(--text2)' }}>{[doc.contact_adresse, doc.contact_cp, doc.contact_ville].filter(Boolean).join(', ')}</p>}
              {doc.contact_email && <p style={{ fontSize: 13, color: 'var(--text2)' }}>{doc.contact_email}</p>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, letterSpacing: '.05em', textTransform: 'uppercase' }}>Dates</p>
              <p style={{ fontSize: 13 }}>Émis le {fmtDate(doc.date_document)}</p>
              {doc.date_echeance && <p style={{ fontSize: 13, color: doc.jours_retard > 0 ? 'var(--danger)' : 'inherit', fontWeight: doc.jours_retard > 0 ? 600 : 400 }}>Échéance : {fmtDate(doc.date_echeance)}{doc.jours_retard > 0 && ` (+${doc.jours_retard}j)`}</p>}
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead><tr><th>Désignation</th><th>Qté</th><th>P.U. HT</th><th>Rem.</th><th>TVA</th><th>Total HT</th></tr></thead>
              <tbody>
                {(doc.lignes || []).map((l, i) => (
                  <tr key={i}>
                    <td><strong>{l.designation}</strong>{l.description && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{l.description}</div>}</td>
                    <td>{l.quantite} {l.unite || ''}</td>
                    <td>{fmt(l.prix_ht)}</td>
                    <td>{parseFloat(l.remise_pct) > 0 ? `${l.remise_pct}%` : '—'}</td>
                    <td>{l.tva_taux}%</td>
                    <td><strong>{fmt(l.total_ht)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="doc-total">
            <table><tbody>
              <tr><td>Total HT</td><td style={{ textAlign: 'right' }}>{fmt(doc.total_ht)}</td></tr>
              {parseFloat(doc.total_remise) > 0 && <tr><td style={{ color: 'var(--danger)' }}>Remise</td><td style={{ textAlign: 'right', color: 'var(--danger)' }}>- {fmt(doc.total_remise)}</td></tr>}
              <tr><td>TVA</td><td style={{ textAlign: 'right' }}>{fmt(doc.total_tva)}</td></tr>
              <tr className="total-row"><td>Total TTC</td><td style={{ textAlign: 'right' }}>{fmt(doc.total_ttc)}</td></tr>
              {parseFloat(doc.montant_regle) > 0 && <>
                <tr><td style={{ color: 'var(--success)' }}>Réglé</td><td style={{ textAlign: 'right', color: 'var(--success)' }}>{fmt(doc.montant_regle)}</td></tr>
                <tr><td style={{ color: 'var(--danger)' }}><strong>Solde dû</strong></td><td style={{ textAlign: 'right', color: 'var(--danger)' }}><strong>{fmt(doc.total_ttc - doc.montant_regle)}</strong></td></tr>
              </>}
            </tbody></table>
          </div>

          {doc.paiements?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, letterSpacing: '.05em', textTransform: 'uppercase' }}>Paiements reçus</p>
              {doc.paiements.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span>{fmtDate(p.date_paiement)} · {p.mode || '—'} {p.reference ? `(${p.reference})` : ''}</span>
                  <strong style={{ color: 'var(--success)' }}>{fmt(p.montant)}</strong>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, letterSpacing: '.05em', textTransform: 'uppercase' }}>Changer le statut</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Object.entries(STATUTS).filter(([v]) => v !== doc.statut).map(([v, l]) => (
                <button key={v} className="btn sm" onClick={() => changeStatut(v)}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {payModal && <PaiementModal docId={id} onClose={() => setPayModal(false)} onSaved={() => { setPayModal(false); reload(); onReload(); toast('Paiement enregistré', 'success') }} />}
    </div>
  )
}

function PaiementModal({ docId, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({ date_paiement: new Date().toISOString().split('T')[0], montant: '', mode: 'Virement' })
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try { await paiementsApi.create({ ...form, document_id: docId }); onSaved() }
    catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" style={{ zIndex: 200 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Enregistrer un paiement</span>

          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <FormGroup label="Date" required><input type="date" className="form-control" value={form.date_paiement} onChange={f('date_paiement')} required /></FormGroup>
            <FormGroup label="Montant (€)" required><input type="number" className="form-control" value={form.montant} onChange={f('montant')} min={0} step="any" required /></FormGroup>
            <FormGroup label="Mode de paiement">
              <select className="form-control" value={form.mode} onChange={f('mode')}>
                {['Virement', 'Chèque', 'Espèces', 'CB', 'Prélèvement', 'Autre'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </FormGroup>
            <FormGroup label="Référence"><input className="form-control" value={form.reference || ''} onChange={f('reference')} placeholder="N° chèque, réf. virement…" /></FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? '…' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
